import {
  type ComponentPublicInstance,
  type MaybeRefOrGetter,
  onBeforeUnmount,
  onMounted,
  type ShallowRef,
  toValue,
} from 'vue';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import type { LegacyGalleryItem } from './legacyGalleryTypes';
import type { LegacyCarouselPager } from './useLegacyCarouselPager';

type TemplateRefElement = Element | ComponentPublicInstance | null;

export const LEGACY_GALLERY_EMPTY_SELECTION: readonly string[] = [];

export interface UseLegacyGalleryFacadeOptions<P extends LegacyGalleryItem> {
  actualItemsPerSlide: MaybeRefOrGetter<number>;
  extras?: object;
  imageElements: ShallowRef<Array<HTMLImageElement | null>>;
  itemElements?: ShallowRef<Array<HTMLElement | null>>;
  items: MaybeRefOrGetter<readonly LegacyGalleryItem[]>;
  pager: LegacyCarouselPager<P>;
}

export interface LegacyGalleryElementSetters {
  setContainerElement: (element: TemplateRefElement) => void;
  setImageElement: (element: TemplateRefElement, index: number) => void;
  setItemElement: (element: TemplateRefElement, index: number) => void;
  setScrollableElement: (element: TemplateRefElement) => void;
}

/**
 * Put the legacy gallery facade in `window.__ImageGallery` while the component
 * is mounted. The visual harness reads this facade.
 *
 * The function puts the property descriptors of `extras` on the common facade,
 * thus a getter in `extras` stays a getter. A key that the common facade also
 * has keeps its position, and a new key goes to the end. The function adds its
 * `onMounted` and `onBeforeUnmount` hooks at the time of the call.
 *
 * `setItemElement` always fills the item list of the pager. `itemElements`
 * changes only the list that `$refs.items` shows.
 */
export function useLegacyGalleryFacade<P extends LegacyGalleryItem>({
  actualItemsPerSlide,
  extras = {},
  imageElements,
  itemElements,
  items,
  pager,
}: UseLegacyGalleryFacadeOptions<P>): LegacyGalleryElementSetters {
  const exposedItemElements = itemElements ?? pager.itemElements;

  function setContainerElement(element: TemplateRefElement): void {
    pager.container.value = element instanceof HTMLElement ? element : null;
  }

  function setScrollableElement(element: TemplateRefElement): void {
    pager.scrollable.value = element instanceof HTMLElement ? element : null;
  }

  function setItemElement(element: TemplateRefElement, index: number): void {
    pager.itemElements.value[index] =
      element instanceof HTMLElement ? element : null;
  }

  function setImageElement(element: TemplateRefElement, index: number): void {
    imageElements.value[index] =
      element instanceof HTMLImageElement ? element : null;
  }

  const facade = {
    $refs: {
      get container(): HTMLElement | null {
        return pager.container.value;
      },
      get images(): Array<HTMLImageElement | null> {
        return imageElements.value;
      },
      get items(): Array<HTMLElement | null> {
        return exposedItemElements.value;
      },
      get scrollable(): HTMLElement | null {
        return pager.scrollable.value;
      },
    },
    get actualItemsPerSlide(): number {
      return toValue(actualItemsPerSlide);
    },
    canScroll: pager.canScroll,
    canSelectItems: false,
    getFirstVisibleItemIndex: pager.getFirstVisibleItemIndex,
    getItemWidth: pager.getItemWidth,
    getVisibleItemUuids: pager.getVisibleItemUuids,
    get hideNextArrow(): boolean {
      return pager.hideNextArrow.value;
    },
    get hidePrevArrow(): boolean {
      return pager.hidePrevArrow.value;
    },
    initialize: pager.initialize,
    isAtEnd: pager.isAtEnd,
    isGridMode: false,
    isItemVisible: pager.isItemVisible,
    itemSelection: null,
    get items(): readonly LegacyGalleryItem[] {
      return toValue(items);
    },
    modelValue: LEGACY_GALLERY_EMPTY_SELECTION,
    onScroll: pager.onScroll,
    scrollToItem: pager.scrollToItem,
    get scrollLeft(): number {
      return pager.scrollLeft.value;
    },
    switchSlide: pager.switchSlide,
    updateArrowVisibility: pager.updateArrowVisibility,
    get visibleItemUuids(): Array<string | undefined> {
      return pager.visibleItemUuids.value;
    },
  };
  Object.defineProperties(facade, Object.getOwnPropertyDescriptors(extras));

  onMounted(() => {
    exposeToGlobalScope(facade, 'ImageGallery');
  });

  onBeforeUnmount(() => {
    const globalWindow = window as unknown as Record<string, unknown>;
    if (globalWindow.__ImageGallery === facade) {
      Reflect.deleteProperty(globalWindow, '__ImageGallery');
    }
  });

  return {
    setContainerElement,
    setImageElement,
    setItemElement,
    setScrollableElement,
  };
}
